<template>
  <div class="pro-color-upsell js-pro-color-upsell">
    <UseSvg
      v-if="!isStandardArtwork"
      icon="pro-color-wheel"
      width="52"
      height="52"
      class="pro-color-upsell__icon"
    />
    <div class="pro-color-upsell__content">
      <div class="pro-color-upsell__info">
        <h3 class="pro-color-upsell__title">
          <template v-if="isStandardArtwork">Need Professional Grade Color?</template>
          <template v-else>Need Professional Grade Color Reproduction?</template>
        </h3>
        <p class="pro-color-upsell__description">
          Try Jiffy ProColor transfers - print in over 16 million colors, for when precision matters
          the most.
        </p>
      </div>

      <a
        type="button"
        class="pro-color-upsell__action"
        :href="linkUrl"
        :class="{
          'pro-color-upsell__action--standard-artwork': isStandardArtwork
        }"
        data-mixpanel-listen="click"
        data-mixpanel-event="pro_color_upsell_cta_click"
        :data-mixpanel-data="eventPayload"
        @click="updateUploadedArtworkIds"
      >
        <UseSvg
          v-if="isStandardArtwork"
          icon="pro-color-wheel"
          width="30"
          height="30"
          class="pro-color-upsell__icon"
          :class="{
            'pro-color-upsell__icon--standard-artwork': isStandardArtwork
          }"
        />
        Try Pro Color
      </a>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from "vue";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { writeCookie } from "common/utils/cookie";
import { initMixpanelEvents } from "common/utils/mixpanel/init-mixpanel-events";
import { useDtfStore } from "./store";

const store = useDtfStore();
const isStandardArtwork = computed(() => store.isStandardArtwork);

const PRO_COLOR_URL = "/jiffytransfers-DTFPC1.html?ac=Transfer";

const props = defineProps({
  artwork: [Object, null]
});

onMounted(() => {
  initMixpanelEvents(".js-pro-color-upsell", "[data-mixpanel-event][data-mixpanel-listen]");
});

const artworkId = computed(() => {
  const { artwork } = props;
  const id = Number(artwork.id) || undefined;

  return id;
});

const eventPayload = computed(() => JSON.stringify({ artwork_id: artworkId.value }));

const linkUrl = computed(() => {
  const { artwork } = props;
  const url = new URLSearchParams(PRO_COLOR_URL);

  if (artworkId.value) {
    const artworkUrls = {};
    artworkUrls[artwork.id] = artwork.url;
    url.append("artwork_urls", encodeURIComponent(JSON.stringify(artworkUrls)));
    url.append("pre_cut", artwork.data.pre_cut);

    if (artwork.metadata) {
      url.append("artwork_height", artwork.height);
      url.append("artwork_width", artwork.width);
      url.append("process_artwork", false);
    }
  }

  return decodeURIComponent(url.toString());
});

const updateUploadedArtworkIds = () => {
  if (artworkId.value) {
    writeCookie("uploaded_artwork_ids", JSON.stringify([artworkId.value]));
  }
};
</script>

<style lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.pro-color-upsell {
  border-radius: 4px;
  border: 1px solid $gray-label;
  color: $black;
  display: flex;
  padding: 12px;
  gap: 12px;
  align-items: flex-start;

  @include with-desktop {
    padding: 16px;
    gap: 16px;
    align-items: center;
  }

  &__icon {
    width: 39px;
    height: 39px;
    flex-shrink: 0;

    &--standard-artwork {
      width: 30px;
      height: 30px;
    }

    @include with-desktop {
      width: 52px;
      height: 52px;

      &--standard-artwork {
        width: 30px;
        height: 30px;
      }
    }
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;

    @include with-desktop {
      flex-direction: row;
      align-items: center;
      gap: 16px;
    }
  }

  &__info {
    flex: 1;
  }

  &__title {
    margin: 0;
    font-size: 14px;
    line-height: 1.14;
    font-weight: 700;
  }

  &__description {
    margin: 4px 0 0;
    font-size: 14px;
    line-height: 1.14;
    font-weight: 400;
  }

  &__action {
    appearance: none;
    border: none;
    cursor: pointer;
    flex-shrink: 0;
    border-radius: 8px;
    background: $primary-light;
    padding: 10px 16px;
    min-width: 150px;
    width: 100%;
    color: $white;
    font-size: 14px;
    line-height: 1.42;
    font-weight: 700;
    text-align: center;

    &:hover {
      background-color: $primary;
      color: $white;
      text-decoration: none;
    }

    @include with-desktop {
      width: auto;
    }

    &--standard-artwork {
      padding: 5px 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
  }
}
</style>
